import { createAppError } from '../../api/errors';
import { mockSuperAdmin } from '../../auth/mockPrincipal';
import { approvalFixtures } from './fixtures';
import type { ApprovalsRepository } from './repository';
import type {
  ApprovalDecisionInput,
  ApprovalDetail,
  ApprovalListQuery,
  FinancialApprovalStatus,
} from './types';

export interface MockApprovalsRepositoryOptions {
  /**
   * Overrides `approval-a`'s requestedBy. Accepts either a raw actor UUID, or the well-known mock
   * principal's username (resolved to that principal's actor id) so tests can express "this request
   * was made by the signed-in mock user" without hardcoding a UUID literal.
   */
  readonly requestedBy?: string;
  readonly status?: FinancialApprovalStatus;
  readonly listError?: boolean;
  readonly detailError?: boolean;
  readonly decideError?: string;
  readonly executeError?: string;
}

const requesterAliases: Readonly<Record<string, string>> = Object.freeze({
  [mockSuperAdmin.username]: mockSuperAdmin.actor.id,
});

export class MockApprovalsRepository implements ApprovalsRepository {
  readonly #options: MockApprovalsRepositoryOptions;
  #approvals: ApprovalDetail[];
  readonly #decisionCommands = new Map<
    string,
    { readonly fingerprint: string; readonly result: { data: ApprovalDetail; requestId: string } }
  >();
  readonly #executionCommands = new Map<
    string,
    { readonly fingerprint: string; readonly result: { data: ApprovalDetail; requestId: string } }
  >();

  constructor(options: MockApprovalsRepositoryOptions = {}) {
    this.#options = options;
    this.#approvals = approvalFixtures.map((item) => this.#applyOverrides(item));
  }

  #applyOverrides(item: ApprovalDetail): ApprovalDetail {
    return {
      ...item,
      requestedBy:
        this.#options.requestedBy === undefined
          ? item.requestedBy
          : resolveRequester(this.#options.requestedBy),
      status: this.#options.status ?? item.status,
    };
  }

  async list(query: ApprovalListQuery, signal: AbortSignal) {
    abortIfNeeded(signal);
    if (this.#options.listError === true) throw mockError('MOCK_APPROVAL_LIST_FAILED', 'list');
    const filtered = this.#approvals.filter((item) => matches(item, query));
    const ordered = [...filtered].sort(
      (left, right) =>
        Date.parse(right.requestedAt) - Date.parse(left.requestedAt) ||
        right.id.localeCompare(left.id),
    );
    return slicePage(ordered, query.cursor, query.pageSize);
  }

  async get(approvalId: string, signal: AbortSignal) {
    abortIfNeeded(signal);
    if (this.#options.detailError === true)
      throw mockError('MOCK_APPROVAL_DETAIL_FAILED', 'detail');
    const item = this.#approvals.find((candidate) => candidate.id === approvalId);
    if (item === undefined) throw mockError('APPROVAL_NOT_FOUND', 'detail');
    return item;
  }

  async decide(approvalId: string, input: ApprovalDecisionInput, idempotencyKey: string) {
    if (this.#options.decideError !== undefined)
      throw mockError(this.#options.decideError, 'decision');
    const fingerprint = JSON.stringify({ approvalId, input });
    const replay = this.#decisionCommands.get(idempotencyKey);
    if (replay !== undefined) {
      if (replay.fingerprint === fingerprint) return replay.result;
      throw mockError('MOCK_APPROVAL_IDEMPOTENCY_CONFLICT', 'decision');
    }
    requireUuid(idempotencyKey, 'decision');
    if (input.reason.trim().length === 0 || input.reason.length > 2000)
      throw mockError('MOCK_APPROVAL_DECISION_INVALID', 'decision');
    const index = this.#approvals.findIndex((item) => item.id === approvalId);
    if (index < 0) throw mockError('APPROVAL_NOT_FOUND', 'decision');
    const current = this.#approvals[index]!;
    if (current.status !== 'PENDING')
      throw mockError('MOCK_APPROVAL_DECISION_CONFLICT', 'decision');
    const updated: ApprovalDetail = {
      ...current,
      status: input.decision === 'APPROVE' ? 'APPROVED' : 'REJECTED',
      decidedBy: mockSuperAdmin.actor.id,
      decidedAt: '2026-07-24T12:00:00+07:00',
      decisionReason: input.reason,
    };
    this.#approvals = this.#approvals.with(index, updated);
    const result = { data: updated, requestId: 'mock-approval-decision-request' };
    this.#decisionCommands.set(idempotencyKey, { fingerprint, result });
    return result;
  }

  async execute(approvalId: string, _input: Record<string, never>, idempotencyKey: string) {
    if (this.#options.executeError !== undefined)
      throw mockError(this.#options.executeError, 'execute');
    const fingerprint = JSON.stringify({ approvalId });
    const replay = this.#executionCommands.get(idempotencyKey);
    if (replay !== undefined) {
      if (replay.fingerprint === fingerprint) return replay.result;
      throw mockError('MOCK_APPROVAL_IDEMPOTENCY_CONFLICT', 'execute');
    }
    requireUuid(idempotencyKey, 'execute');
    const index = this.#approvals.findIndex((item) => item.id === approvalId);
    if (index < 0) throw mockError('APPROVAL_NOT_FOUND', 'execute');
    const current = this.#approvals[index]!;
    if (current.status !== 'APPROVED')
      throw mockError('MOCK_APPROVAL_EXECUTION_CONFLICT', 'execute');
    const updated: ApprovalDetail = {
      ...current,
      status: 'EXECUTED',
      executedAt: '2026-07-24T13:00:00+07:00',
      executionReference: `mock-execution-${approvalId}`,
    };
    this.#approvals = this.#approvals.with(index, updated);
    const result = { data: updated, requestId: 'mock-approval-execution-request' };
    this.#executionCommands.set(idempotencyKey, { fingerprint, result });
    return result;
  }
}

export function createApprovalsRepositoryMock(
  options: MockApprovalsRepositoryOptions = {},
): ApprovalsRepository {
  return new MockApprovalsRepository(options);
}

function resolveRequester(value: string): string {
  return requesterAliases[value] ?? value;
}

function matches(item: ApprovalDetail, query: ApprovalListQuery): boolean {
  const f = query.filters;
  return (
    (f.partnerId.trim() === '' || item.partnerId === f.partnerId.trim()) &&
    (f.actionType === '' || item.actionType === f.actionType) &&
    (f.status === '' || item.status === f.status) &&
    (f.entityType.trim() === '' || item.entityType === f.entityType.trim()) &&
    (f.entityId.trim() === '' || item.entityId === f.entityId.trim())
  );
}

function slicePage(items: readonly ApprovalDetail[], cursor: string, limit: number) {
  const match = /^mock-approval-cursor-(\d+)$/u.exec(cursor);
  const start = match === null ? 0 : Number(match[1]);
  const pageSize = Math.max(1, Math.min(100, limit));
  const end = start + pageSize;
  return {
    items: items.slice(start, end),
    nextCursor: end < items.length ? `mock-approval-cursor-${end}` : null,
    requestId: 'mock-approval-request',
  };
}

function requireUuid(value: string, surface: string) {
  const uuidV4Pattern = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/iu;
  if (!uuidV4Pattern.test(value)) throw mockError('MOCK_APPROVAL_IDEMPOTENCY_INVALID', surface);
}

function abortIfNeeded(signal: AbortSignal) {
  if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
}

function mockError(code: string, surface: string) {
  return createAppError({
    status: 500,
    code,
    message: `Unable to load mock approval ${surface}`,
    requestId: `mock-approval-${surface}-request`,
    fieldErrors: {},
    retryAfterSeconds: null,
  });
}
